import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CtySpinner from '../spinner/CtySpinner';
import {
    ServerPaginationPanel,
    serverSideFilterComponents,
    serverSideFilterContext,
    serverSideSortColDef,
    type ServerPaginationPanelProps,
} from './serverPagination';
import { useTheme } from '../context/ThemeContext';
import {
    getTableState,
    setTableState,
    captureAgGridState,
    restoreAgGridState,
    applyPersistedTableStateToColumnDefs,
    hasAgGridRestorationPayload,
    type PersistedTableState,
} from './tableStatePersistence';

const DEFAULT_PAGINATION_PAGE_SIZE = 20;

interface AgGridTableProps {
    tableData: any[];
    /** Server-side pagination: `tableData` is the current page only, already sorted and filtered by the
     * server. The footer shows these page/total numbers and reports page changes back. */
    serverPagination?: ServerPaginationPanelProps;
    tableColumnDefs: any[];
    /** Unique id for this table. When set, table state (filters, sort, column width/order) is persisted in localStorage. */
    tableId?: string;
    /** Called after persisted state is applied. Optional: the grid UI (sort, filters, column state) is restored
     * automatically for all tables. Only pass this for server-side tables that need to refetch with restored
     * sort/filters (e.g. set your state and refetch using saved.sort and saved.filterModel). */
    onStateRestored?: (saved: PersistedTableState) => void;
    onRowClicked?: (event: any) => void;
    onPaginationChanged?: (event: any) => void;
    onFilterChanged?: (event: any) => void;
    onSortChanged?: (event: any) => void;
    onGridReady?: (event: any) => void;
    disableRowClick?: boolean;
    useAutoHeight?: boolean;
    containerStyle?: React.CSSProperties;
    containerClassName?: string;
    isLoading?: boolean;
}

const SAVE_DEBOUNCE_MS = 300;

function getRowClass(disableRowClick: boolean, hasRowClick: boolean): string {
  if (disableRowClick) return 'cursor-progress';
  return hasRowClick ? 'cursor-pointer' : '';
}

const AgGridTable = (props: AgGridTableProps) => {

    const { tableData, serverPagination, tableColumnDefs = [], tableId, onStateRestored, onRowClicked, onPaginationChanged, onSortChanged, disableRowClick, onFilterChanged, onGridReady: onGridReadyProp, useAutoHeight = true, containerStyle, containerClassName, isLoading = false } = props;

    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const gridApiRef = useRef<any>(null);
    const [appliedSort, setAppliedSort] = useState<{ field: string; order: string } | null>(null);
    const { isDark } = useTheme();

    useEffect(() => () => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    }, []);

    const gridStyle = useMemo(() => ({
        height: "100%",
        width: "100%",
        minHeight: 400,
        '--ag-border-color': isDark ? '#252B32' : '#e0e0e0',
        '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
        ...(containerStyle ?? {})
    } as React.CSSProperties), [containerStyle, isDark]);

    const paginationPageSizeSelector = useMemo(() => {
        return [20, 40, 50];
    }, []);

    const isServerPaged = Boolean(serverPagination);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        ...(isServerPaged ? serverSideSortColDef : {}),
    }), [isServerPaged]);

    const saveState = useCallback((api: any) => {
        if (!tableId || !api) return;
        try {
            const state = captureAgGridState(api);
            setTableState(tableId, state);
        } catch {
            // ignore
        }
    }, [tableId]);

    const debouncedSaveState = useCallback((api: any) => {
        if (!tableId || !api) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            saveTimeoutRef.current = null;
            saveState(api);
        }, SAVE_DEBOUNCE_MS);
    }, [tableId, saveState]);

    const onGridReady = useCallback((params: any) => {
        const api = params.api;
        gridApiRef.current = api;

        if (tableId) {
            const saved = getTableState(tableId);
            if (saved && Object.keys(saved).length > 0) {
                const doRestore = () => {
                    restoreAgGridState(api, saved);
                    if (saved.sort?.field && saved.sort?.order) setAppliedSort(saved.sort);
                    if (hasAgGridRestorationPayload(saved)) onStateRestored?.(saved);
                };
                setTimeout(doRestore, 0);
            } else {
                api.sizeColumnsToFit?.();
            }
            const saveOnChange = () => debouncedSaveState(api);
            api.addEventListener('filterChanged', saveOnChange);
            api.addEventListener('sortChanged', () => {
                saveOnChange();
                const model = api.getSortModel?.();
                if (model?.[0]) setAppliedSort({ field: model[0].colId, order: model[0].sort });
                else setAppliedSort(null);
            });
            api.addEventListener('columnResized', saveOnChange);
            api.addEventListener('columnMoved', saveOnChange);
            api.addEventListener('columnVisible', saveOnChange);
        } else {
            api.sizeColumnsToFit?.();
        }

        onGridReadyProp?.(params);
    }, [tableId, onStateRestored, onGridReadyProp, debouncedSaveState]);

    // Computed during render (not in an effect) so the grid has its columns from the first render.
    // Otherwise a persisted filter restored on grid ready can run before columns exist and be
    // silently dropped by setFilterModel, leaving the API filtered but the floating filter empty.
    const columnDefs = useMemo(
        () => applyPersistedTableStateToColumnDefs(tableColumnDefs, tableId, appliedSort),
        [tableColumnDefs, tableId, appliedSort]
    );

    const rowsClickable = Boolean(onRowClicked && !disableRowClick);

    return (
        <div
            className={`ag-theme-alpine ${containerClassName ?? ''} relative ${rowsClickable ? 'doc-ag-grid-rows-clickable' : ''}`}
            style={isServerPaged ? { ...gridStyle, display: 'flex', flexDirection: 'column' } : gridStyle}
        >
            {rowsClickable && (
                <style>{`.doc-ag-grid-rows-clickable .ag-row .ag-cell { cursor: pointer; }`}</style>
            )}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-content1 bg-opacity-70 z-10">
                    <CtySpinner size="md" />
                </div>
            )}
            <div style={isServerPaged ? { flex: 1, minHeight: 0 } : { height: '100%' }}>
            <AgGridReact
                suppressDragLeaveHidesColumns={true}
                rowData={tableData ?? []}
                {...(isServerPaged ? { components: serverSideFilterComponents, context: serverSideFilterContext } : {})}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                headerHeight={40}
                domLayout={undefined}
                onGridReady={onGridReady}
                onRowClicked={disableRowClick ? undefined : onRowClicked}
                pagination={!isServerPaged}
                paginationPageSize={DEFAULT_PAGINATION_PAGE_SIZE}
                paginationPageSizeSelector={paginationPageSizeSelector}
                onPaginationChanged={onPaginationChanged}
                onSortChanged={onSortChanged}
                alwaysShowHorizontalScroll={true}
                onFilterChanged={onFilterChanged}
                rowClass={getRowClass(!!disableRowClick, !!onRowClicked)}
            />
            </div>
            {serverPagination && <ServerPaginationPanel pageSizeOptions={paginationPageSizeSelector} {...serverPagination} />}
        </div>
    );
};

export { AgGridTable };
